import type { HeadingLevel, MarkdownSegment } from "./types";

function isFence(line: string) {
  return /^```[A-Za-z0-9_-]*\s*$/.test(line.trim());
}

function parseFenceLanguage(line: string) {
  const match = line.trim().match(/^```([A-Za-z0-9_-]+)?\s*$/);
  return match?.[1] ?? null;
}

function isTableLine(line: string) {
  const trimmed = line.trim();
  return trimmed.startsWith("|") && trimmed.includes("|", 1);
}

function isBlockStart(line: string) {
  const trimmed = line.trim();

  return (
    trimmed === "" ||
    isFence(trimmed) ||
    isTableLine(trimmed) ||
    /^#{1,4}\s+/.test(trimmed) ||
    /^[-*]\s+/.test(trimmed) ||
    /^\d+[.)]\s+/.test(trimmed)
  );
}

function collectCodeBlock(lines: string[], startIndex: number) {
  const language = parseFenceLanguage(lines[startIndex]);
  const block: string[] = [];
  let index = startIndex + 1;

  while (index < lines.length && !isFence(lines[index])) {
    block.push(lines[index]);
    index += 1;
  }

  return {
    nextIndex: index < lines.length ? index + 1 : index,
    segment: {
      type: "code",
      language,
      content: block.join("\n"),
    } satisfies MarkdownSegment,
  };
}

function collectTable(lines: string[], startIndex: number) {
  const block: string[] = [];
  let index = startIndex;

  while (index < lines.length && isTableLine(lines[index])) {
    block.push(lines[index]);
    index += 1;
  }

  return {
    nextIndex: index,
    segment: { type: "table", lines: block } satisfies MarkdownSegment,
  };
}

function collectList(
  lines: string[],
  startIndex: number,
  markerPattern: RegExp,
  markerRemovalPattern: RegExp,
  type: "unordered-list" | "ordered-list",
) {
  const items: string[] = [];
  let index = startIndex;

  while (index < lines.length && markerPattern.test(lines[index].trim())) {
    items.push(lines[index].trim().replace(markerRemovalPattern, ""));
    index += 1;
  }

  return {
    nextIndex: index,
    segment: { type, items } satisfies MarkdownSegment,
  };
}

function collectParagraph(lines: string[], startIndex: number) {
  const block: string[] = [];
  let index = startIndex;

  while (index < lines.length && !isBlockStart(lines[index])) {
    block.push(lines[index]);
    index += 1;
  }

  return {
    nextIndex: index,
    segment: {
      type: "paragraph",
      content: block.join("\n"),
    } satisfies MarkdownSegment,
  };
}

export function parseMarkdown(content: string): MarkdownSegment[] {
  const lines = content.split("\n");
  const segments: MarkdownSegment[] = [];
  let index = 0;

  while (index < lines.length) {
    const trimmed = lines[index].trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (isFence(trimmed)) {
      const result = collectCodeBlock(lines, index);
      segments.push(result.segment);
      index = result.nextIndex;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      segments.push({
        type: "heading",
        level: headingMatch[1].length as HeadingLevel,
        text: headingMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    if (isTableLine(trimmed)) {
      const result = collectTable(lines, index);
      segments.push(result.segment);
      index = result.nextIndex;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const result = collectList(
        lines,
        index,
        /^[-*]\s+/,
        /^[-*]\s+/,
        "unordered-list",
      );
      segments.push(result.segment);
      index = result.nextIndex;
      continue;
    }

    if (/^\d+[.)]\s+/.test(trimmed)) {
      const result = collectList(
        lines,
        index,
        /^\d+[.)]\s+/,
        /^\d+[.)]\s+/,
        "ordered-list",
      );
      segments.push(result.segment);
      index = result.nextIndex;
      continue;
    }

    const result = collectParagraph(lines, index);
    segments.push(result.segment);
    index = result.nextIndex;
  }

  return segments;
}
